import { useState, useMemo } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, message, Space, Row, Col, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ClearOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { invoicesAPI, customersAPI } from '../api/endpoints';
import { formatCurrency, formatDate, formatPeriod, getInvoiceStatusColor, getInvoiceStatusLabel } from '../utils/helpers';
import dayjs from 'dayjs';
import { Tag } from 'antd';

const STATUS_OPTIONS = [
  { value: 'draft',           label: 'Borrador' },
  { value: 'issued',          label: 'Emitida' },
  { value: 'scheduled',       label: 'Programada' },
  { value: 'partially_paid',  label: 'Parcialmente Pagada' },
  { value: 'overdue',         label: 'Vencida' },
  { value: 'cancelled',       label: 'Cancelada' },
];

const InvoicesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const [filterText, setFilterText]       = useState('');
  const [filterCustomer, setFilterCustomer] = useState(null);
  const [filterStatus, setFilterStatus]   = useState(null);

  const { data: invoicesData, refetch } = useQuery(['invoices', { page_size: 1000 }], () => invoicesAPI.list({ page_size: 1000 }));
  const { data: customersData } = useQuery('customers-list', () => customersAPI.list());
  const createMutation = useMutation(invoicesAPI.create);
  const updateMutation = useMutation(({ id, data }) => invoicesAPI.update(id, data));
  const deleteMutation = useMutation(invoicesAPI.delete);

  const allInvoices = (invoicesData?.data?.results || []).filter((inv) => inv.status !== 'paid');
  const customers = customersData?.data?.results || [];

  const invoices = useMemo(() => {
    return allInvoices.filter((inv) => {
      if (filterText && !inv.invoice_number.toLowerCase().includes(filterText.toLowerCase())) return false;
      if (filterCustomer && inv.customer !== filterCustomer) return false;
      if (filterStatus && inv.status !== filterStatus) return false;
      return true;
    });
  }, [allInvoices, filterText, filterCustomer, filterStatus]);

  const hasActiveFilters = filterText || filterCustomer || filterStatus;

  const columns = [
    {
      title: 'Factura',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      sorter: (a, b) => a.invoice_number.localeCompare(b.invoice_number),
    },
    {
      title: 'Cliente',
      dataIndex: 'customer_name',
      key: 'customer_name',
      sorter: (a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''),
    },
    {
      title: 'Período',
      key: 'service_period',
      render: (_, record) => formatPeriod(record.service_period_from, record.service_period_to),
      sorter: (a, b) => a.service_period_from.localeCompare(b.service_period_from),
    },
    {
      title: 'Monto',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => formatCurrency(amount),
      sorter: (a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount),
      align: 'right',
    },
    {
      title: 'Vencimiento',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date) => formatDate(date),
      sorter: (a, b) => a.due_date.localeCompare(b.due_date),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getInvoiceStatusColor(status)}>
          {getInvoiceStatusLabel(status)}
        </Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button type="primary" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      issue_date: dayjs(record.issue_date),
      due_date: dayjs(record.due_date),
      service_period_from: dayjs(record.service_period_from),
      service_period_to: dayjs(record.service_period_to),
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Eliminar Factura',
      content: '¿Está seguro que desea eliminar esta factura?',
      okText: 'Sí',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(id);
          message.success('Factura eliminada correctamente');
          refetch();
        } catch (error) {
          message.error('Error al eliminar factura');
        }
      },
    });
  };

  const handleDownloadCSV = () => {
    const headers = ['Factura', 'Cliente', 'Período', 'Monto', 'Vencimiento', 'Estado'];
    const rows = invoices.map((inv) => [
      inv.invoice_number,
      inv.customer_name,
      formatPeriod(inv.service_period_from, inv.service_period_to),
      inv.total_amount,
      inv.due_date,
      getInvoiceStatusLabel(inv.status),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `facturas_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    form.resetFields();
  };

  const onFinish = async (values) => {
    try {
      const data = {
        ...values,
        invoice_number: values.invoice_number?.trim(),
        issue_date: values.issue_date.format('YYYY-MM-DD'),
        due_date: values.due_date.format('YYYY-MM-DD'),
        service_period_from: values.service_period_from.format('YYYY-MM-DD'),
        service_period_to: values.service_period_to.format('YYYY-MM-DD'),
      };

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data });
        message.success('Factura actualizada correctamente');
      } else {
        await createMutation.mutateAsync(data);
        message.success('Factura creada correctamente');
      }
      handleCloseModal();
      refetch();
    } catch (error) {
      const errors = error?.response?.data;
      if (errors && typeof errors === 'object') {
        const firstField = Object.keys(errors)[0];
        const msg = Array.isArray(errors[firstField]) ? errors[firstField][0] : errors[firstField];
        message.error(msg || 'Error al guardar factura');
      } else {
        message.error('Error al guardar factura');
      }
    }
  };

  return (
    <div>
      <Card
        title="Gestión de Facturas"
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadCSV}
            >
              Descargar CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(null);
                form.resetFields();
                setIsModalOpen(true);
              }}
            >
              Nueva Factura
            </Button>
          </Space>
        }
      >
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col flex="220px">
            <Input
              placeholder="Buscar por número..."
              prefix={<SearchOutlined />}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              allowClear
            />
          </Col>
          <Col flex="220px">
            <Select
              placeholder="Filtrar por cliente"
              style={{ width: '100%' }}
              allowClear
              value={filterCustomer}
              onChange={setFilterCustomer}
              options={customers.map((c) => ({ value: c.id, label: c.business_name }))}
            />
          </Col>
          <Col flex="200px">
            <Select
              placeholder="Filtrar por estado"
              style={{ width: '100%' }}
              allowClear
              value={filterStatus}
              onChange={setFilterStatus}
              options={STATUS_OPTIONS}
            />
          </Col>
          {hasActiveFilters && (
            <Col>
              <Button
                icon={<ClearOutlined />}
                onClick={() => { setFilterText(''); setFilterCustomer(null); setFilterStatus(null); }}
              >
                Limpiar
              </Button>
            </Col>
          )}
        </Row>
        <Table dataSource={invoices} columns={columns} rowKey="id" />
      </Card>

      <Modal
        title={editingId ? 'Editar Factura' : 'Nueva Factura'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={handleCloseModal}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={createMutation.isLoading || updateMutation.isLoading}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="customer"
            label="Cliente"
            rules={[{ required: true, message: 'Por favor selecciona un cliente' }]}
          >
            <Select placeholder="Selecciona un cliente">
              {customers.map((customer) => (
                <Select.Option key={customer.id} value={customer.id}>
                  {customer.business_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="invoice_number"
            label="Número de Factura"
            rules={[{ required: true, message: 'Por favor ingresa el número' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Descripción">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Monto Base"
                rules={[{ required: true, message: 'Por favor ingresa el monto' }]}
              >
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tax_amount" label="Impuestos">
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="issue_date"
                label="Fecha de Emisión"
                rules={[{ required: true, message: 'Por favor selecciona una fecha' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="due_date"
                label="Fecha de Vencimiento"
                rules={[{ required: true, message: 'Por favor selecciona una fecha' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="service_description"
            label="Descripción del Servicio"
            rules={[{ required: true, message: 'Por favor ingresa la descripción' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="service_period_from"
                label="Período del Servicio - Desde"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="service_period_to"
                label="Período del Servicio - Hasta"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="status"
            label="Estado"
            initialValue="draft"
          >
            <Select>
              <Select.Option value="draft">Borrador</Select.Option>
              <Select.Option value="issued">Emitida</Select.Option>
              <Select.Option value="scheduled">Programada</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvoicesPage;
